/**
 * 环境配置管理
 * 支持本地/线上环境动态切换
 */

const LOCAL_API_ORIGIN = 'http://127.0.0.1:9999'

// 环境配置
const envConfig = {
  // 本地开发环境
  local: {
    name: '本地环境',
    baseURL: `${LOCAL_API_ORIGIN}/api`,
    wsURL: 'ws://127.0.0.1:9999/ws',
    timeout: 30000
  },
  // 线上生产环境（部署时通过 nginx 反向代理 /api → 后端）
  production: {
    name: '线上环境',
    baseURL: '/api',
    wsURL: 'wss://123.wwyxc.cn/ws',
    timeout: 30000
  },
  // 测试环境（可选）
  test: {
    name: '测试环境',
    baseURL: 'https://q.wwyxc.xyz/api',
    wsURL: 'wss://q.wwyxc.xyz/ws',
    timeout: 30000
  }
}

/**
 * 获取当前环境
 * 优先级：localStorage > 默认配置
 */
export function getCurrentEnv() {
  // 从localStorage获取用户选择的环境
  const savedEnv = localStorage.getItem('API_ENV')
  
  // 如果有保存的环境配置，使用保存的
  if (savedEnv && envConfig[savedEnv]) {
    return savedEnv
  }
  
  // 默认根据域名判断
  const hostname = window.location.hostname

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local'
  } else if (hostname.includes('test')) {
    return 'test'
  } else {
    return 'production'
  }
}

/**
 * 获取当前环境配置
 */
export function getEnvConfig() {
  const currentEnv = getCurrentEnv()
  return envConfig[currentEnv]
}

/**
 * 切换环境
 * @param {string} env - 环境名称：local, production, test
 */
export function switchEnv(env) {
  if (!envConfig[env]) {
    console.error(`环境 ${env} 不存在`)
    return false
  }
  
  // 保存到localStorage
  localStorage.setItem('API_ENV', env)
  
  // 刷新页面以应用新环境
  window.location.reload()
  
  return true
}

/**
 * 获取所有可用环境
 */
export function getAllEnvs() {
  return Object.keys(envConfig).map(key => ({
    key,
    ...envConfig[key]
  }))
}

/**
 * 获取环境名称
 */
export function getEnvName() {
  return getEnvConfig().name
}

/**
 * 获取API基础URL
 */
export function getBaseURL() {
  return getEnvConfig().baseURL
}

function getOriginFromBaseURL(baseURL) {
  const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : ''
  if (!baseURL) return fallbackOrigin

  if (/^https?:\/\//i.test(baseURL)) {
    try {
      return new URL(baseURL).origin
    } catch (error) {
      return fallbackOrigin
    }
  }

  return fallbackOrigin
}

export function getCurrentApiOrigin() {
  return getOriginFromBaseURL(getBaseURL())
}

export function getLocalApiOrigin() {
  return getOriginFromBaseURL(envConfig.local?.baseURL)
}

export function isBrowserLocalhost() {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

export function getPreferredUploadBaseURL() {
  return isBrowserLocalhost() ? envConfig.local.baseURL : getBaseURL()
}

/**
 * 获取WebSocket URL
 */
export function getWsURL() {
  return getEnvConfig().wsURL
}

/**
 * 获取请求超时时间
 */
export function getTimeout() {
  return getEnvConfig().timeout
}

/**
 * 检查是否为本地环境
 */
export function isLocalEnv() {
  return getCurrentEnv() === 'local'
}

/**
 * 检查是否为生产环境
 */
export function isProductionEnv() {
  return getCurrentEnv() === 'production'
}

export default {
  getCurrentEnv,
  getEnvConfig,
  switchEnv,
  getAllEnvs,
  getEnvName,
  getBaseURL,
  getWsURL,
  getTimeout,
  isLocalEnv,
  isProductionEnv
}

